// Mitteilungszentrum nach Themen (01.11.2026, vom User abgenommenes Zielbild).
//
// EINE reine, DOM-freie Funktion für Dashboard-Karte UND Glocke: Sie ordnet
// ungelesene Mitteilungen, die ungelesenen Posteingangs-Mails und die offenen
// verpassten Anrufe in eine FESTE Reihenfolge von Gruppen und Abschnitten:
//
//   Mails (Zu Vorgängen · Geteilt & erwähnt · Posteingang ungelesen)
//   Verpasste Anrufe
//   Vorgänge (Erwähnungen · Fristen & Wiedervorlagen · Neu im Vorgang)
//   Chat · Tickets · Freigaben · System
//
// Bewusst KEINE Zusammenfassung von Einträgen: jede Zeile ist genau eine Mail,
// ein Anruf (alle Versuche derselben Nummer) oder eine Mitteilung. Gruppen und
// Abschnitte ohne Einträge fallen weg.
import type { NotificationDto, NotificationMeta } from "../../services/notificationService";

export type GruppeKey = "mails" | "anrufe" | "vorgaenge" | "chat" | "tickets" | "freigaben" | "system";

export type AbschnittKey =
    | "mail_vorgang"
    | "mail_geteilt"
    | "posteingang"
    | "anrufe"
    | "erwaehnungen"
    | "fristen"
    | "neu_im_vorgang"
    | "chat"
    | "tickets"
    | "freigaben"
    | "system";

/** Eine ungelesene Mail aus dem Posteingang (nicht aus der Notification-Tabelle). */
export type PosteingangMail = {
    mailbox: string;
    id: string;
    internetMessageId?: string | null;
    absender: string;
    betreff: string;
    /** Anfang des Mailtexts (Graph `bodyPreview`), leer wenn keiner da ist. */
    vorschau: string;
    empfangen: string;
    /**
     * Graphs `meetingMessageType` (meetingRequest, meetingCancelled,
     * meetingAccepted …); `null` bei einer gewöhnlichen Mail. Termin-Mails
     * stehen ganz normal im Posteingang, nur als Termin-Zeile dargestellt.
     */
    terminArt?: string | null;
};

/** Das Nötigste eines Anrufs (Teilmenge von `CallLogDto`). */
export type AnrufRoh = {
    id: number;
    fromNumber?: string | null;
    contactId?: number | null;
    contactDisplayName?: string | null;
    startedAt: string;
};

/** Ein verpasster Anruf als Zeile: alle offenen Versuche derselben Nummer. */
export type VerpassterAnruf = {
    key: string;
    nummer: string | null;
    kontaktId: number | null;
    kontaktName: string | null;
    /** Zeit des letzten Versuchs (ISO). */
    zeit: string;
    anzahl: number;
    callIds: number[];
};

export type FristStufe = "ueberfaellig" | "heute" | "woche" | "spaeter";

export type MzEintrag =
    | { art: "mitteilung"; key: string; n: NotificationDto; frist?: FristStufe }
    | { art: "mail"; key: string; mail: PosteingangMail }
    | { art: "termin"; key: string; mail: PosteingangMail }
    | { art: "anruf"; key: string; anruf: VerpassterAnruf };

export type MzAbschnitt = {
    key: AbschnittKey;
    titel: string;
    eintraege: MzEintrag[];
    /** Kurzzusammenfassung rechts im Abschnittskopf (z. B. Fristen-Stufen). */
    zusammenfassung?: string;
    /** „Alle abhaken" möglich (nur Mitteilungen; nicht Posteingang/Anrufe). */
    abhakbar: boolean;
    /** Seitenweise statt „weitere anzeigen" (Posteingang, 10 je Seite). */
    seitenweise: boolean;
};

export type MzGruppe = {
    key: GruppeKey;
    titel: string;
    anzahl: number;
    zusammenfassung: string;
    abschnitte: MzAbschnitt[];
    /** Abschnittsköpfe zeigen (Mails, Vorgänge) oder nur die Zeilen. */
    mitAbschnittskoepfen: boolean;
    abhakbar: boolean;
    standardOffen: boolean;
};

export type MzCluster = {
    gruppen: MzGruppe[];
    /** Ungelesene Mitteilungen, die in den Gruppen stehen (Badge der Glocke). */
    anzahlMitteilungen: number;
    leer: boolean;
};

/* ────────── Typzuordnung ────────── */

const MAIL_VORGANG = new Set(["mail_vorgang", "vorgang_mail_reply", "mail_vorgang_eigen", "mail_digest", "mail_incoming", "mail"]);
const MAIL_GETEILT = new Set(["mail-share", "mail_mention"]);
const ERWAEHNUNGEN = new Set(["mention"]);
const FRISTEN = new Set(["twv", "frist", "twv_heute"]);
const NEU_IM_VORGANG = new Set([
    "dokument_neu",
    "vorgang_neu",
    "vorgang_zuweisung",
    "todo_neu",
    "todo_zuweisung",
    "reply",
    "kommentar_neu",
    "vorgang_regel_neu",
]);
const CHAT = new Set(["chat_dm", "chat_nachricht", "chat_mention"]);
const FREIGABEN = new Set(["approval_request", "approval_decision"]);

/** In welchen Abschnitt gehört ein Notification-Typ? Unbekanntes → System. */
export function mitteilungsAbschnitt(type: string | null | undefined): AbschnittKey {
    const t = String(type ?? "");
    if (MAIL_VORGANG.has(t)) return "mail_vorgang";
    if (MAIL_GETEILT.has(t)) return "mail_geteilt";
    if (ERWAEHNUNGEN.has(t)) return "erwaehnungen";
    if (FRISTEN.has(t)) return "fristen";
    if (NEU_IM_VORGANG.has(t)) return "neu_im_vorgang";
    if (CHAT.has(t)) return "chat";
    if (t.startsWith("ticket_")) return "tickets";
    if (FREIGABEN.has(t)) return "freigaben";
    return "system";
}

/* ────────── Datum ────────── */

const zwei = (n: number) => String(n).padStart(2, "0");
/** Lokales Datum als YYYY-MM-DD. */
export function tagVon(d: Date): string {
    return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}
/** Sonntag der laufenden Woche (Mo–So), lokal. */
function wochenende(jetzt: Date): string {
    const d = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
    const tag = (d.getDay() + 6) % 7; // Mo = 0
    d.setDate(d.getDate() + (6 - tag));
    return tagVon(d);
}
const zeitVon = (iso: string | null | undefined): number => {
    const t = new Date(iso ?? "").getTime();
    return Number.isNaN(t) ? 0 : t;
};

/**
 * Fälligkeits-Stufe einer Wiedervorlage/Frist. Mit `meta.faelligAm` exakt;
 * ohne (Altbestand): `frist` ist immer überfällig (sie entsteht erst nach
 * Ablauf), eine Wiedervorlage gilt am Tag ihrer Mitteilung als fällig.
 */
export function fristStufe(n: Pick<NotificationDto, "type" | "createdAt" | "meta">, jetzt: Date): FristStufe {
    const heute = tagVon(jetzt);
    let faellig = n.meta?.faelligAm ?? null;
    if (!faellig) {
        if (n.type === "frist") return "ueberfaellig";
        const d = new Date(n.createdAt);
        faellig = Number.isNaN(d.getTime()) ? heute : tagVon(d);
    }
    if (faellig < heute) return "ueberfaellig";
    if (faellig === heute) return "heute";
    if (faellig <= wochenende(jetzt)) return "woche";
    return "spaeter";
}

const FRIST_RANG: Record<FristStufe, number> = { ueberfaellig: 0, heute: 1, woche: 2, spaeter: 3 };

/** „3 überfällig · 5 heute · 4 diese Woche" (Nullen entfallen). */
export function fristZusammenfassung(stufen: FristStufe[]): string {
    const z = { ueberfaellig: 0, heute: 0, woche: 0, spaeter: 0 } as Record<FristStufe, number>;
    for (const s of stufen) z[s]++;
    return [
        z.ueberfaellig ? `${z.ueberfaellig} überfällig` : "",
        z.heute ? `${z.heute} heute` : "",
        z.woche ? `${z.woche} diese Woche` : "",
        z.spaeter ? `${z.spaeter} später` : "",
    ]
        .filter(Boolean)
        .join(" · ");
}

/* ────────── Anrufe ────────── */

const nummernSchluessel = (nr: string | null | undefined): string => String(nr ?? "").replace(/[^\d+]/g, "");

/**
 * Offene verpasste Anrufe je Gegenstelle zu EINER Zeile: gleiche Nummer (sonst
 * gleicher Kontakt) = ein Anrufer, der es mehrmals versucht hat. Neuester
 * Versuch zuerst.
 */
export function gruppiereVerpassteAnrufe(calls: readonly AnrufRoh[]): VerpassterAnruf[] {
    const map = new Map<string, VerpassterAnruf>();
    const sortiert = [...calls].sort((a, b) => zeitVon(b.startedAt) - zeitVon(a.startedAt));
    for (const c of sortiert) {
        const nr = nummernSchluessel(c.fromNumber);
        const key = nr ? `nr:${nr}` : c.contactId ? `k:${c.contactId}` : `id:${c.id}`;
        const vorhanden = map.get(key);
        if (vorhanden) {
            vorhanden.anzahl++;
            vorhanden.callIds.push(c.id);
            if (!vorhanden.kontaktName && c.contactDisplayName) {
                vorhanden.kontaktName = c.contactDisplayName;
                vorhanden.kontaktId = c.contactId ?? null;
            }
            continue;
        }
        map.set(key, {
            key,
            nummer: c.fromNumber?.trim() || null,
            kontaktId: c.contactId ?? null,
            kontaktName: c.contactDisplayName?.trim() || null,
            zeit: c.startedAt,
            anzahl: 1,
            callIds: [c.id],
        });
    }
    return [...map.values()];
}

/* ────────── Vorgangs-Mail-Zeile ────────── */

const ROLLE_TEXT: Record<NonNullable<NotificationMeta["rolle"]>, string> = {
    inhaber: "deiner",
    hauptverantwortlich: "du: Hauptverantwortlich",
    bearbeiter: "du: Bearbeiter",
    zu_informieren: "du: zu informieren",
};

export type VorgangsMailTeile = {
    absender: string | null;
    betreff: string | null;
    vorgang: string | null;
    inhaber: string | null;
    rolle: string | null;
    /** To-do im Vorgang, an dem die Mail hängt; `null` = Mail hängt am Vorgang selbst. */
    todo: string | null;
};

/** Die Bausteine der Zeile „Absender · „Betreff" · Vorgang „…" (DC) · du: …". */
export function vorgangsMailTeile(
    n: Pick<NotificationDto, "title" | "body" | "meta"> & Partial<Pick<NotificationDto, "todoTitel">>,
): VorgangsMailTeile {
    const m = n.meta ?? null;
    let absender = m?.mail?.absender?.trim() || null;
    let betreff = m?.mail?.betreff?.trim() || null;
    // Altbestand ohne meta: `body` ist „Absender: Betreff" (Auto-Link) bzw. der Betreff.
    if (!m?.mail && n.body) {
        const i = n.body.indexOf(": ");
        if (i > 0) {
            absender = n.body.slice(0, i).trim() || null;
            betreff = n.body.slice(i + 2).trim() || null;
        } else betreff = n.body.trim() || null;
    }
    const titelVorgang = /„([^"“]*)["“]\s*$/.exec(n.title ?? "")?.[1] ?? null;
    return {
        absender,
        betreff,
        vorgang: m?.vorgang?.titel?.trim() || titelVorgang,
        inhaber: m?.vorgang?.inhaberKuerzel?.trim() || null,
        rolle: m?.rolle ? ROLLE_TEXT[m.rolle] : null,
        // Das Backend löst das To-do beim Lesen auf (`todoTitel`); fehlt das
        // Feld ganz (älterer Server), hilft das beim Anlegen gespeicherte.
        todo:
            (n.todoTitel !== undefined ? n.todoTitel?.trim() : m?.todo?.titel?.trim()) || null,
    };
}

/**
 * Zweite Zeile einer gewöhnlichen Mitteilung: Kunde · Text · Auslöser. Den
 * Auslöser nicht, wenn er schon im Titel steht — beim Chat ist der Titel der
 * Absender („Name" bzw. „Name in „Gruppe""), sonst stünde er doppelt da
 * („Test · Name"). In der Darstellung entdoppelt, damit auch der Bestand
 * richtig aussieht.
 */
export function mitteilungsZweiteZeile(
    n: Pick<NotificationDto, "title" | "body" | "actorName" | "kundeName">,
): string | null {
    const titel = (n.title ?? "").trim().toLocaleLowerCase("de-DE");
    const actor = (n.actorName ?? "").trim();
    const actorImTitel = !!actor && titel.includes(actor.toLocaleLowerCase("de-DE"));
    const teile = [n.kundeName, n.body, actorImTitel ? null : actor].map((x) => (x ?? "").trim()).filter(Boolean);
    return teile.length ? teile.join(" · ") : null;
}

/* ────────── Cluster ────────── */

const GRUPPEN: Array<{ key: GruppeKey; titel: string; abschnitte: AbschnittKey[]; standardOffen: boolean; koepfe: boolean }> = [
    { key: "mails", titel: "Mails", abschnitte: ["mail_vorgang", "mail_geteilt", "posteingang"], standardOffen: true, koepfe: true },
    { key: "anrufe", titel: "Verpasste Anrufe", abschnitte: ["anrufe"], standardOffen: true, koepfe: false },
    { key: "vorgaenge", titel: "Vorgänge", abschnitte: ["erwaehnungen", "fristen", "neu_im_vorgang"], standardOffen: true, koepfe: true },
    { key: "chat", titel: "Chat", abschnitte: ["chat"], standardOffen: false, koepfe: false },
    { key: "tickets", titel: "Tickets", abschnitte: ["tickets"], standardOffen: false, koepfe: false },
    { key: "freigaben", titel: "Freigaben", abschnitte: ["freigaben"], standardOffen: false, koepfe: false },
    { key: "system", titel: "System", abschnitte: ["system"], standardOffen: false, koepfe: false },
];

const ABSCHNITT_TITEL: Record<AbschnittKey, string> = {
    mail_vorgang: "Zu Vorgängen",
    mail_geteilt: "Geteilt & erwähnt",
    posteingang: "Posteingang · ungelesen",
    anrufe: "Verpasste Anrufe",
    erwaehnungen: "Erwähnungen",
    fristen: "Fristen & Wiedervorlagen",
    neu_im_vorgang: "Neu im Vorgang",
    chat: "Chat",
    tickets: "Tickets",
    freigaben: "Freigaben",
    system: "System",
};

const klein = (s: string | null | undefined) => String(s ?? "").trim().toLowerCase();
const mehrzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;

function gruppenZusammenfassung(key: GruppeKey, abschnitte: MzAbschnitt[], jetzt: Date): string {
    const anz = (k: AbschnittKey) => abschnitte.find((a) => a.key === k)?.eintraege.length ?? 0;
    const mitteilungen = abschnitte.flatMap((a) => a.eintraege).filter((e): e is Extract<MzEintrag, { art: "mitteilung" }> => e.art === "mitteilung");
    switch (key) {
        case "mails":
            return [
                anz("mail_vorgang") ? `${anz("mail_vorgang")} zu Vorgängen` : "",
                anz("mail_geteilt") ? `${anz("mail_geteilt")} geteilt` : "",
                anz("posteingang") ? `${anz("posteingang")} ungelesen` : "",
            ]
                .filter(Boolean)
                .join(" · ");
        case "anrufe": {
            const erster = abschnitte[0]?.eintraege[0];
            if (!erster || erster.art !== "anruf") return "";
            const d = new Date(erster.anruf.zeit);
            if (Number.isNaN(d.getTime())) return "";
            const uhr = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
            return tagVon(d) === tagVon(jetzt) ? `zuletzt heute ${uhr}` : `zuletzt ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${uhr}`;
        }
        case "vorgaenge":
            return [
                anz("erwaehnungen") ? mehrzahl(anz("erwaehnungen"), "Erwähnung", "Erwähnungen") : "",
                anz("fristen") ? mehrzahl(anz("fristen"), "Frist", "Fristen") : "",
                anz("neu_im_vorgang") ? `${anz("neu_im_vorgang")} neu` : "",
            ]
                .filter(Boolean)
                .join(" · ");
        case "chat": {
            const namen = [...new Set(mitteilungen.map((e) => e.n.actorName?.trim()).filter(Boolean))] as string[];
            if (!namen.length) return "";
            return `von ${namen.slice(0, 2).join(", ")}${namen.length > 2 ? ` und ${namen.length - 2} weiteren` : ""}`;
        }
        default: {
            const t = mitteilungen[0]?.n.title?.trim();
            return t ? `zuletzt: ${t}` : "";
        }
    }
}

/**
 * Gliedert alles Offene nach Themen. Gelesene Mitteilungen (`readAt`) fallen
 * raus; eine Posteingangs-Mail, die schon als Vorgangs-Mail-Mitteilung steht,
 * erscheint nicht doppelt (Abgleich über `internetMessageId`, sonst
 * Postfach + Graph-Id — best effort, die Graph-Id ist ordnerabhängig).
 */
export function clusterNotifications(input: {
    notifications: readonly NotificationDto[];
    posteingang?: readonly PosteingangMail[];
    anrufe?: readonly VerpassterAnruf[];
    jetzt?: Date;
}): MzCluster {
    const jetzt = input.jetzt ?? new Date();
    const offen = input.notifications.filter((n) => !n.readAt);

    const proAbschnitt = new Map<AbschnittKey, MzEintrag[]>();
    const push = (k: AbschnittKey, e: MzEintrag) => {
        const l = proAbschnitt.get(k);
        if (l) l.push(e);
        else proAbschnitt.set(k, [e]);
    };

    const neuesteZuerst = [...offen].sort((a, b) => zeitVon(b.createdAt) - zeitVon(a.createdAt) || b.id - a.id);
    const bekannteMails = new Set<string>();
    for (const n of neuesteZuerst) {
        const k = mitteilungsAbschnitt(n.type);
        if (k === "fristen") push(k, { art: "mitteilung", key: `n-${n.id}`, n, frist: fristStufe(n, jetzt) });
        else push(k, { art: "mitteilung", key: `n-${n.id}`, n });
        if (k === "mail_vorgang" && n.meta?.mail) {
            const m = n.meta.mail;
            if (m.internetMessageId) bekannteMails.add(`imid:${klein(m.internetMessageId)}`);
            if (m.mailbox && m.messageId) bekannteMails.add(`id:${klein(m.mailbox)}|${m.messageId}`);
        }
    }
    const fristen = proAbschnitt.get("fristen");
    if (fristen) {
        fristen.sort((a, b) => {
            const ra = a.art === "mitteilung" && a.frist ? FRIST_RANG[a.frist] : 9;
            const rb = b.art === "mitteilung" && b.frist ? FRIST_RANG[b.frist] : 9;
            // Überfällige zuerst, sonst neueste zuerst (die Liste ist schon nach Zeit sortiert).
            return (ra === 0 ? 0 : 1) - (rb === 0 ? 0 : 1);
        });
    }

    const posteingang = [...(input.posteingang ?? [])]
        .filter(
            (m) =>
                !(m.internetMessageId && bekannteMails.has(`imid:${klein(m.internetMessageId)}`)) &&
                !bekannteMails.has(`id:${klein(m.mailbox)}|${m.id}`),
        )
        .sort((a, b) => zeitVon(b.empfangen) - zeitVon(a.empfangen));
    for (const m of posteingang) {
        const key = `m-${m.mailbox}|${m.id}`;
        if (m.terminArt) push("posteingang", { art: "termin", key, mail: m });
        else push("posteingang", { art: "mail", key, mail: m });
    }
    for (const a of input.anrufe ?? []) push("anrufe", { art: "anruf", key: `a-${a.key}`, anruf: a });

    const gruppen: MzGruppe[] = [];
    let anzahlMitteilungen = 0;
    for (const g of GRUPPEN) {
        const abschnitte: MzAbschnitt[] = [];
        for (const k of g.abschnitte) {
            const eintraege = proAbschnitt.get(k) ?? [];
            if (!eintraege.length) continue;
            const abhakbar = k !== "posteingang" && k !== "anrufe";
            if (abhakbar) anzahlMitteilungen += eintraege.length;
            abschnitte.push({
                key: k,
                titel: ABSCHNITT_TITEL[k],
                eintraege,
                abhakbar,
                seitenweise: k === "posteingang",
                zusammenfassung:
                    k === "fristen"
                        ? fristZusammenfassung(eintraege.map((e) => (e.art === "mitteilung" && e.frist ? e.frist : "heute")))
                        : undefined,
            });
        }
        if (!abschnitte.length) continue;
        gruppen.push({
            key: g.key,
            titel: g.titel,
            anzahl: abschnitte.reduce((s, a) => s + a.eintraege.length, 0),
            zusammenfassung: gruppenZusammenfassung(g.key, abschnitte, jetzt),
            abschnitte,
            mitAbschnittskoepfen: g.koepfe,
            abhakbar: abschnitte.some((a) => a.abhakbar),
            standardOffen: g.standardOffen,
        });
    }
    return { gruppen, anzahlMitteilungen, leer: gruppen.length === 0 };
}

/** Ids aller abhakbaren Mitteilungen einer Gruppe bzw. eines Abschnitts. */
export function abhakbareIds(abschnitte: readonly MzAbschnitt[]): number[] {
    return abschnitte
        .filter((a) => a.abhakbar)
        .flatMap((a) => a.eintraege)
        .flatMap((e) => (e.art === "mitteilung" ? [e.n.id] : []));
}
